import ControlFactory from '@/Factories/ControlFactory';
import AssociationTreeViewT from '@/Controls/AssociationTreeView';
import DropDownTreeViewViewModelT from '@/ViewModels/Controls/DropDownTreeViewViewModel';
import DropDownTreeViewItemViewModelT from '@/ViewModels/Controls/DropDownTreeViewItemViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import AssociationTreeViewViewModelT from '@/ViewModels/Controls/AssociationTreeViewViewModel';
import ComponentManager from '@/Management/ComponentManager';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import FilterHelper from '@/Utility/FilterHelper';
import { IIteratorDTO } from 'coreframetypings/dto';
import { IAssociationTreeViewFactoryConstructorParameter } from '#/DefaultViewExtensionInterfaces';
import TreeViewItemViewModel from '@/ViewModels/Controls/TreeViewItemViewModel';

export default class AssociationTreeViewFactory extends ControlFactory {
    private DropDownTreeViewViewModel = ComponentManager.getViewModel<typeof DropDownTreeViewViewModelT>('DropDownTreeViewViewModel');
    private DropDownTreeViewItemViewModel = ComponentManager.getViewModel<typeof DropDownTreeViewItemViewModelT>('DropDownTreeViewItemViewModel');
    private AssociationTreeViewViewModel = ComponentManager.getViewModel<typeof AssociationTreeViewViewModelT>('AssociationTreeViewViewModel');
    private AssociationTreeView = ComponentManager.getControl<typeof AssociationTreeViewT>('AssociationTreeView');
    
    /** 
     * @override
     */
    public control: AssociationTreeViewT;
    
    /**
     * @override
     */
    public viewModel: AssociationTreeViewViewModelT;
    
    public orderStatement: string;
    public selectableIndex: number;
    public parentIdIndex: number;
    public captionIndex: number;
    public allItems: KnockoutObservable<string[][]>;
    public allValues: KnockoutObservable<string[][]>;
    public defaultIterator: IIteratorDTO;
    public iterator: IIteratorDTO;
    public dataIterator: IIteratorDTO;
    public isRefreshing: boolean;
    public ignoreValueOnCheckChange: string;
    public showOnlySelectablePath: boolean;
    public dropDownTreeView: DropDownTreeViewViewModelT;
    
    public onConnectItemHandler: (value: string) => JQuery.Promise<void>;
    public onDisconnectItemHandler: (value: string) => void;
    public evaluateValues: () => void;
    public evaluateItems: () => void;
    
    private readonly dataBusy: KnockoutObservable<boolean>;
    private readonly valueBusy: KnockoutObservable<boolean>;
    private readonly serverCommunication: KnockoutObservable<boolean>;
    private readonly lazyLoading: boolean;
    
    private readonly _onConnectItemHandler: (value: string) => void;
    private readonly _onDisconnectItemHandler: (value: string) => void;

    private jQueryControl: JQuery;
    private isOpen: KnockoutObservable<boolean>;
    private allDataIdentifier = 'all';
    
    constructor(protected params: IAssociationTreeViewFactoryConstructorParameter) {
        super(params);
        
        this._onDisconnectItemHandler = (value: string): void => {
            this.onDisconnectItemHandler(value);
        }
        
        this._onConnectItemHandler = (value: string): void => {
            let deferred = this.onConnectItemHandler(value);
            $.when(deferred).then(() => {
                this._evaluateValues();
            });
        };
        
        this.lazyLoading = this.paramOrDefault(params.lazyLoading, false);
        this.showOnlySelectablePath = this.paramOrDefault(params.showOnlySelectablePath, false);
        
        // falls kein this.orderStatement übergeben wird, wird später nach ObjectIdentValue sortiert
        this.orderStatement = this.paramOrDefault(params.orderStatement, '');
        
        this.dataBusy = this.observableOrDefault(false);
        this.valueBusy = this.observableOrDefault(false);
        this.serverCommunication = this.observableOrDefault(false);
        this.allItems = this.observableOrDefault([]) as KnockoutObservable<string[][]>;
        this.allValues = this.observableOrDefault([]) as KnockoutObservable<string[][]>;
        this.isOpen = this.observableOrDefault(this.params.isOpen, false);

        let setBusy = () => {
            let isBusy = this.dataBusy() || this.valueBusy() || this.serverCommunication();
            this.isBusy(isBusy);
        }
        
        this.defineSubscription(this.dataBusy, setBusy);
        this.defineSubscription(this.valueBusy, setBusy);
        this.defineSubscription(this.serverCommunication, setBusy);
        
        this.defaultIterator = this.context.defaultIterators[this.detailViewItem.name];
        this.iterator = null;
        this.dataIterator = null;
        this.isRefreshing = false;
        this.ignoreValueOnCheckChange = null;
        this.selectableIndex = -1;
        this.parentIdIndex = -1;
        this.captionIndex = 1;
        
        this.context.genericObject.suppressNotificationFor(this.detailViewItem.name + '.PDO_ID');
        this.context.genericObject.suppressNotificationFor(this.detailViewItem.name + '.ObjectIdentValue');
        
        this.onConnectItemHandler = ((value: string) => { // value is the 'toPdoId'
            if (this.ignoreValueOnCheckChange === value || this.isRefreshing) {
                this.ignoreValueOnCheckChange = null;
                this.viewModel.dropDownTreeView.coreFrameDropDownTreeViewViewModel.isExpanded(false);
                return undefined;
            }
            return $.when(this.context.notifyBeforeUpdatedAssociation(this.detailViewItem.listView.relationName, 'connect', { toPdoIds: [value] }))
                .then((doUpdateAssociation) => {
                    let doNotPropagate = !(doUpdateAssociation == null || doUpdateAssociation === true);
                    if (doNotPropagate) {
                        return $.Deferred().resolve();
                    }
                    let deferred = $.Deferred();
                    this.serverCommunication(true);
                    RADEnvironmentProxy.getInstance().connect({
                        pdoID: this.context.genericObject.pdoId,
                        assoziationName: this.detailViewItem.listView.relationName,
                        topdTypeName: this.detailViewItem.listView.typeName,
                        toPDOID: value,
                        nestedContextName: this.context.getNestedContextPath()
                    }).then((changeObject) => {
                        if (this.isDisposed)
                            return;
                        if (this.validateStatus(changeObject)) {
                            this.context.genericObject.isModified(true);
                            this.context.notifyUpdatedAssociation(this.detailViewItem.listView.relationName);
                            this.context.notifyFactoryRefreshed(this.detailViewItem);
                        }
                        else {
                            this.ignoreValueOnCheckChange = value;
                            this.viewModel.dropDownTreeView.coreFrameDropDownTreeViewViewModel.isExpanded(false);
                        }
                        deferred.resolve();
                    }).always(() => {
                        if (this.isDisposed)
                            return;
                        this.serverCommunication(false);
                    });
                    return deferred;
                });
        });
        
        this.onDisconnectItemHandler = ((value) => {
            if (this.ignoreValueOnCheckChange === value || this.isRefreshing) {
                this.ignoreValueOnCheckChange = null;
                return;
            }
            
            this.serverCommunication(true);
            
            RADEnvironmentProxy.getInstance().tryToDisconnectObject({
                pdoID: this.context.genericObject.pdoId,
                assoziationName: this.detailViewItem.listView.relationName,
                topdTypeName: this.detailViewItem.listView.typeName,
                pdoIDs: [value]
            }).then((changeObject) => {
                if (this.isDisposed) {
                    return null;
                }
                
                if (this.validateStatus(changeObject)) {
                    return $.when(this.context.notifyBeforeUpdatedAssociation(this.detailViewItem.listView.relationName,
                        'disconnect', { toPdoIds: [value] }))
                        .then((doUpdateAssociation) => {
                            if (doUpdateAssociation == null || doUpdateAssociation === true) {
                                return RADEnvironmentProxy.getInstance().disconnect({
                                    pdoID: this.context.genericObject.pdoId,
                                    assoziationName: this.detailViewItem.listView.relationName,
                                    topdTypeName: this.detailViewItem.listView.typeName,
                                    toPDOID: value,
                                    nestedContextName: this.context.getNestedContextPath()
                                });
                            } else {
                                this._evaluateValues();
                                return null;
                            }
                        });
                } else {
                    this.ignoreValueOnCheckChange = value;
                    return null;
                }
            }).then((changeObject) => {
                if (this.validateStatus(changeObject)) {
                    this.context.genericObject.isModified(true);
                    this.context.notifyUpdatedAssociation(this.detailViewItem.listView.relationName);
                    this.context.notifyFactoryRefreshed(this.detailViewItem);
                } else {
                    this.ignoreValueOnCheckChange = value;
                }
                this._evaluateValues();
            }).always(() => {
                if (this.isDisposed)
                    return;
                this.serverCommunication(false);
            });
        });
        
        this.registerForMessageId('RefreshAssociationsMessage', () => {
            if (this.viewModel != null) {
                this.isRefreshing = true;
                $.when(this._evaluateItems(), this._evaluateValues()).then(() => {
                    this.isRefreshing = false;
                });
            }
        });
    }

    /**
     * @override
     */
    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }
        
        let dropDownTreeViewVM = this.createDropDownTreeViewViewModel();
        this.viewModel = new this.AssociationTreeViewViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            invalidationText: this.invalidationText,
            isBusy: this.isBusy,
            dropDownTreeView: dropDownTreeViewVM
        });
        
        this.control = new this.AssociationTreeView(this.viewModel);
        this.jQueryControl = this.control.getTuple().control;
        this.registerObservableValidation(this.viewModel.dropDownTreeView.values);
        
        return this.jQueryControl;
    }
    
    public notifyDetailViewClosed(topdTypeName?: string, toPDOID?: string | number): void {
        this._evaluateItems();
        this._evaluateValues();
        this.context.genericObject.isModified(true);
        this.context.notifyUpdatedAssociation(this.detailViewItem.listView.relationName);
        this.context.notifyFactoryRefreshed(this.detailViewItem);
    }
    
    public createDropDownTreeViewValues(resultSetColumns?: string): KnockoutAsyncComputed<JQueryPromise<string[][]> | string[][]> {
        return this.defineAsyncComputed((): JQueryPromise<string[][]> | string[][] => {
            return this.requestAssociationData(resultSetColumns);
        }, [], true);
    }
    
    public requestAssociationData(resultSetColumns?: string): JQueryPromise<string[][]> {
        this.valueBusy(true);
        this.deleteIterators([this.iterator]);

        return $.when(this.getIterator()).then((iterator) => {
            if (this.isDisposed)
                return null;
            return this.onIteratorLoaded(iterator, resultSetColumns);
        }).always(() => {
            if (this.isDisposed)
                return;
            this.valueBusy(false);
        });
    }
    
    public async loadData(): Promise<string[][]> {
        let properties = ['ObjectIdentValue'];
        if (this.params.connectItemIdent != null) {
            this.captionIndex = 2;
            properties.push(this.params.connectItemIdent);
        }

        if (this.params.parentAssociationName != null) {
            properties.push(this.params.parentAssociationName + '.PDO_ID');
            this.parentIdIndex = properties.length;
        }

        if (this.params.selectableIndicatorName != null) {
            properties.push(this.params.selectableIndicatorName);
            this.selectableIndex = properties.length;
        }

        this.deleteIterators([this.dataIterator]);
        const iterator = await RADEnvironmentProxy.getInstance().createIteratorWithOperator({
            pdTypeName: this.detailViewItem.listView.typeName,
            properties: properties,
            filter: JSON.stringify(
                FilterHelper.createFilterObjectForStaticFilter(this.detailViewItem.listView
                    .staticFilterExpression)),
            orderby: this.convertOrderStatementWithoutAssociation(this.orderStatement),
            retrieveExtentCount: true,
            contextPDO_ID: this.isSummaryViewMode ? "" + -1 : this.context.genericObject.pdoId,
            itOperator: {
                includeAllPrincipals: this.detailViewItem.listView.includeAllPrincipals,
                includeBasePrincipals: this.detailViewItem.listView.includeBasePrincipals,
                includeSubPrincipals: this.detailViewItem.listView.includeSubPrincipals,
                memoryOptimizationEnabled: true,
                onlySubPrincipals: null,
                historizationDates: null,
                isGridIterator: null,
                principalFilter: null,
                searchObjectPositionRequest: null,
                visibleDeletedObjects: null,
                hideConnectedElementsRequest: null
            }
        });
        this.dataIterator = iterator;
        if (this.isDisposed)
            return null;
        if (+iterator.extentCount > 0) {
            return RADEnvironmentProxy.getInstance().getExtentWithPDOIDAsFirstColumn({
                iteratorID: iterator.id,
                start: 0,
                end: +iterator.extentCount - 1
            });
        }
        return [];
    }
    
    public processArrayOfGeneric(arrayOfGeneric: string[][]): Array<DropDownTreeViewItemViewModelT> {
        if (arrayOfGeneric == null && this.isDisposed) {
            return [];
        }

        let result = [];
        // wenn kein this.orderStatement übergeben wurde, wird jetzt nach ObjectIdentValue sortiert.
        if (this.orderStatement === '') {
            arrayOfGeneric = arrayOfGeneric.sort((a, b) => {
                if (a[1].toLowerCase() < b[1].toLowerCase())
                    return -1;
                if (a[1].toLowerCase() > b[1].toLowerCase())
                    return 1;
                return 0;
            });
        }
        if (this.showOnlySelectablePath) {
            // leere Strukturelemente rausfiltern
            arrayOfGeneric = ((arrayOfGeneric) => {
                let indexToDelete = [];
                for (let i = 0; i < arrayOfGeneric.length; i++) {
                    let item = arrayOfGeneric[i];
                    // ist es ein strukturelement ohne kinder
                    if (this.isStructurElementWithoutChildren(arrayOfGeneric,
                        item,
                        this.selectableIndex,
                        this.parentIdIndex)) {
                        indexToDelete.push(i);
                    }
                }
                indexToDelete = indexToDelete.reverse();
                for (let i = 0; i < indexToDelete.length; i++) {
                    arrayOfGeneric.splice(indexToDelete[i], 1);
                }
                return arrayOfGeneric;
            })(arrayOfGeneric);
        }
        for (let i = 0; i < arrayOfGeneric.length; i++) {
            let caption = arrayOfGeneric[i][this.captionIndex];
            let loc = this.detailViewItem.name + '.' + i;
            let item = new this.DropDownTreeViewItemViewModel({
                parentLocator: this.locator,
                locator: loc,
                isEnabled: arrayOfGeneric[i][this.selectableIndex],
                value: arrayOfGeneric[i][0],
                parentItem: arrayOfGeneric[i][this.parentIdIndex],
                link: new this.LinkViewModel({
                    parentLocator: this.locator,
                    locator: loc + '.link',
                    label: new this.LabelViewModel({
                        parentLocator: this.locator,
                        locator: loc + '.link.label',
                        caption: caption
                    })
                })
            });
            result.push(item);
        }
        return result;
    }
    
    public processResult(result: Array<DropDownTreeViewItemViewModelT>): Array<DropDownTreeViewItemViewModelT> {
        if (result == null)
            return [];

        const structure = this.buildStructure(result);
        const circularity = this.getCircularity(result);

        if (circularity.length > 0) {
            let text = _.map(circularity,
                (item: TreeViewItemViewModel) => {
                    return item.link.label.caption();
                }).join('; ');
            (this.viewModel.dropDownTreeView.label.postfix as KnockoutObservable<string> | LabelViewModelT).caption('\n' + text);
            this.viewModel.dropDownTreeView.label.isVisible(true);
        } else {
            this.viewModel.dropDownTreeView.label.isVisible(false);
        }

        this.viewModel.dropDownTreeView.circularities = circularity;
        return structure;
    }
    
    public deleteIterators(iterators: Array<IIteratorDTO>): void {
        if (this.isDisposed || iterators == null) {
            return;
        }
        iterators = iterators.filter((it) => { return it != null; });
        if (iterators != null && iterators.length > 0) {
            RADEnvironmentProxy.getInstance().deleteIterators({
                iteratorIDs: iterators.map((it) => { return parseInt(it.id); })
            });
        }
    }
    
    protected disposeInternal(): void {
        this.deleteIterators([this.defaultIterator, this.dataIterator, this.iterator]);
        super.disposeInternal();
    }
    
    private createDropDownTreeViewViewModel(): DropDownTreeViewViewModelT {
        let valuesObs = this.observableOrDefault([]) as KnockoutObservableArray<string>;
        let completeValuesObs = this.observableOrDefault([]) as KnockoutObservable<string[][]>;
        let itemsObs = this.observableOrDefault([]) as KnockoutObservableArray<DropDownTreeViewItemViewModelT>;
        this.dropDownTreeView = new this.DropDownTreeViewViewModel({
            items: itemsObs,
            values: valuesObs,
            fullValues: completeValuesObs,
            label: this.createInvalidCircularyLabel(),
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            isFocusable: this.isFocusable,
            isBusy: this.isBusy,
            onChecked: this._onConnectItemHandler,
            onUnchecked: this._onDisconnectItemHandler,
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            lazyLoading: this.lazyLoading,
            tooltip: new this.LabelViewModel({
                ergName: this.detailViewItem.tooltip
            }),
            onOpen: () => {
                if (this.isOpen()) {
                    return;
                }

                this.isOpen(true);
                if (this.lazyLoading) {
                    this.requestData(valuesObs, completeValuesObs, itemsObs);
                }
            }
        });

        // create async-computeds for further updates of values and items
        let values = this.createDropDownTreeViewValues(this.allDataIdentifier);
        let items = this.createDropDownTreeViewItems();
        this.defineSubscription(values, (valuesUpdate: string[][]) => { valuesObs(valuesUpdate.map((entry) => { return entry[0] })); });
        this.defineSubscription(items, (itemsUpdate) => {
            itemsUpdate.then((update) => itemsObs(update));
        });
        this.evaluateValues = (() => { return values.evaluate(); })
        this.evaluateItems = (() => { return items.evaluate(); })

        if (this.lazyLoading) {
            // Wir laden nur (möglich) verbundenen Items zur Darstellung im Control
            this.requestAssociationData(this.allDataIdentifier).then((valuesUpdate) => {
                if (this.isDisposed)
                    return null;

                valuesObs(valuesUpdate.map((entry) => { return entry[0] }));
                completeValuesObs(valuesUpdate);
            });
            return this.dropDownTreeView;
        }
        
        this.requestData(valuesObs, completeValuesObs, itemsObs);
        return this.dropDownTreeView;
    }
    
    private createDropDownTreeViewItems(): KnockoutAsyncComputed<Promise<DropDownTreeViewItemViewModelT[]>> {
        return this.defineAsyncComputed(async (): Promise<DropDownTreeViewItemViewModelT[]> => {
            if (this.lazyLoading && !this.isOpen())
                return [];
            
            return await this.requestItems();
        }, Promise.resolve([]), true);
    }
    
    private requestData(valuesObs: KnockoutObservableArray<string>, 
                        completeValuesObs: KnockoutObservable<string[][]>, 
                        itemsObs: KnockoutObservableArray<DropDownTreeViewItemViewModelT>) {
        $.Deferred().resolve()
            // request values
            .then(() => {
                if (this.isDisposed)
                    return null;
                
                if (this.isOpen())
                    return completeValuesObs();
                
                return this.requestAssociationData(this.allDataIdentifier);
            })
            // set values to this.viewModel, request items
            .then((valuesUpdate) => {
                if (this.isDisposed)
                    return null;
                valuesObs(valuesUpdate.map((entry) => { return entry[0] }));
                completeValuesObs(valuesUpdate);
                return this.requestItems();
            })
            // set items to this.viewModel
            .then((itemsUpdate: Array<DropDownTreeViewItemViewModelT>) => {
                if (this.isDisposed)
                    return;
                itemsObs(itemsUpdate);
            });
    }
    
    private requestItems(): Promise<Array<DropDownTreeViewItemViewModelT>> {
        this.dataBusy(true);
        return this.loadData().then((arrayOfGeneric) => {
            if (this.isDisposed)
                return null;
            this.allItems(arrayOfGeneric);
            return this.processArrayOfGeneric(arrayOfGeneric);
        }).then((result: Array<DropDownTreeViewItemViewModelT>) => {
            if (this.isDisposed)
                return null;
            return this.processResult(result);
        }).finally(() => {
            if (this.isDisposed)
                return;
            this.dataBusy(false);
        });
    }
    private createInvalidCircularyLabel(): LabelViewModelT {
        return new this.LabelViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.label',
            isVisible: false,
            ergName: 'TREEVIEW_INVALID_CIRCULARY_LINKED_LIST',
            postfix: new this.LabelViewModel({
                caption: ''
            }),
            isErrorLabel: true
        });
    }
    
    private _evaluateValues(): void {
        this.evaluateValues();
    }
    
    private _evaluateItems(): void {
        this.evaluateItems();
    }
    
    private getIterator(): JQueryPromise<IIteratorDTO> {
        return RADEnvironmentProxy.getInstance().createIteratorForAssoziation({
            pdTypeName: this.context.genericObject.pdTypeName,
            pdo_id: this.context.genericObject.pdoId,
            assoziationName: this.detailViewItem.name,
            properties: ['ObjectIdentValue'],
            retrieveExtentCount: true,
            itOperator: {
                includeAllPrincipals: this.detailViewItem.listView.includeAllPrincipals,
                includeBasePrincipals: this.detailViewItem.listView.includeBasePrincipals,
                includeSubPrincipals: this.detailViewItem.listView.includeSubPrincipals,
                memoryOptimizationEnabled: true,
                onlySubPrincipals: null,
                historizationDates: null,
                isGridIterator: null,
                principalFilter: null,
                searchObjectPositionRequest: null,
                visibleDeletedObjects: null,
                hideConnectedElementsRequest: null
            },
            filter: '',
            orderby: ''
        });
    }
    
    private onIteratorLoaded(iterator: IIteratorDTO, resultSetColumns: string): JQueryPromise<string[][]> | string[][] {
        this.iterator = iterator;
        if (+iterator.extentCount > 0) {
            // requests a complete dataset of e.g. 'ISMS.ISMS.Kataloge.Katalogmassnahme' pdoId, KM-...id, title, parentTitle, isChecked
            return RADEnvironmentProxy.getInstance().getExtentWithPDOIDAsFirstColumn({
                iteratorID: iterator.id,
                start: 0,
                end: +iterator.extentCount - 1
            }).then((arrayOfGeneric) => {
                if (this.isDisposed || arrayOfGeneric == null)
                    return undefined;

                let result = [];
                this.allValues(arrayOfGeneric);
                for (let i = 0; i < arrayOfGeneric.length; i++) {
                    if (!resultSetColumns) {
                        result.push(arrayOfGeneric[i][0]); // pdoId is at index '0'
                    } else if (resultSetColumns === this.allDataIdentifier) {
                        result.push(arrayOfGeneric[i]);
                    }
                }
                return result;
            });
        }
        else {
            return [];
        }
    }
    
    private isStructurElementWithoutChildren(arrayOfGeneric: string[][], element: Array<string>, selectableIndex: number, parentIdIndex: number): boolean | undefined {
        if (!element[selectableIndex]) {
            let findResult = ((arrayofGeneric, element, selectableIndex, parentIdIndex) => {
                // kind-Element gefunden: prüfen ob es sich auch um ein Strukturelement ohne Kinder handelt (rekursiv)
                for (let i = 0; i < arrayOfGeneric.length; i++) {
                    let itemToCompare = arrayOfGeneric[i];
                    // wenn das Item ein Child von element ist
                    if (itemToCompare[parentIdIndex] === element[0]) {
                        // wenn es sich dabei auch um ein Strukturelement handelt
                        if (!itemToCompare[selectableIndex]) {
                            let hasNoChildren = this.isStructurElementWithoutChildren(arrayOfGeneric, itemToCompare, selectableIndex, parentIdIndex);
                            if (!hasNoChildren) {
                                return true;
                            }
                        }
                        else {
                            return true;
                        }
                    }
                }
                return undefined;
            })(arrayOfGeneric, element, selectableIndex, parentIdIndex);
            if (findResult === undefined) {
                return true;
            }
        }
        return false;
    }
    
    private buildStructure(items: Array<DropDownTreeViewItemViewModelT>): Array<DropDownTreeViewItemViewModelT> {
        for (let i = 0; i < items.length; i++) {
            let rootItem = items[i];
            let rootItemValue = rootItem.value();
            for (let j = 0; j < items.length; j++) {
                let childItem = items[j];
                let parentValue = childItem.parentItem;
                if (parentValue === rootItemValue) {
                    rootItem.items.push(childItem);
                }
            }
        }
        
        return _.filter(items, (item: DropDownTreeViewItemViewModelT) => {
            return item.parentItem == null;
        });
    }
    
    private getCircularity(items: Array<DropDownTreeViewItemViewModelT>): Array<DropDownTreeViewItemViewModelT> {
        let result = [];
        for (let i = 0; i < items.length; i++) {
            let doubleItem = this.checkParentChain(items, items[i]);
            if (doubleItem != null && result.indexOf(doubleItem) === -1) {
                result.push(doubleItem);
            }
        }
        return result;
    }
    
    private checkParentChain(items: Array<DropDownTreeViewItemViewModelT>, 
                             item: DropDownTreeViewItemViewModelT, 
                             chain?: Array<DropDownTreeViewItemViewModelT>): DropDownTreeViewItemViewModelT {
        if (chain == null) {
            chain = [];
        }
        if (item.parentItem == null) {
            return null;
        }
        let parent = _.find(items, (oItem: DropDownTreeViewItemViewModelT) => {
            return oItem.value() === item.parentItem;
        });
        if (parent == null) {
            return null;
        }
        chain.push(parent.value());
        let unique = _.uniq(chain);
        if (unique.length !== chain.length) {
            let sorted = chain.sort();
            for (let i = 0; i < chain.length - 1; i++) {
                if (sorted[i + 1] == sorted[i]) {
                    return _.find(items, (item) => {
                        return item.value() === sorted[i];
                    });
                }
            }
        }
        
        return this.checkParentChain(items, parent, chain);
    }
}

ComponentManager.setFactory('AssociationTreeView', AssociationTreeViewFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');
ComponentManager.setFactory('RADPresentation.Core.UIComponents.Factories.AssociationTreeView', AssociationTreeViewFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');